/**
 * Settings → Training tab content (admin/manager only).
 *
 * Two-pane layout:
 *   LEFT  — module library (list, create, click to edit)
 *   RIGHT — assignment dashboard (who's done what, sortable, with bulk-assign)
 *
 * ModuleEditor opens in a modal when the user clicks a module card or
 * "+ New module".
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  TrainingModule, TrainingAssignment,
  listModules, createModule, listAllAssignments, assignModule,
  updateModule,
} from '../../api/training';
import { listStaff } from '../../api/staff';
import type { StaffMember } from '../../types/staff';
import { ModuleEditor } from './ModuleEditor';

export function TrainingAdmin() {
  const [modules, setModules] = useState<TrainingModule[]>([]);
  const [assignments, setAssignments] = useState<TrainingAssignment[]>([]);
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editTarget, setEditTarget] = useState<number | 'new' | null>(null);
  const [assigningModuleId, setAssigningModuleId] = useState<number | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [mods, assigns, stf] = await Promise.all([
        listModules(true),
        listAllAssignments(),
        listStaff(),
      ]);
      setModules(mods);
      setAssignments(assigns);
      setStaff(stf);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Quick metrics for the dashboard header
  const metrics = {
    total: assignments.length,
    completed: assignments.filter(a => a.status === 'COMPLETED').length,
    overdue: assignments.filter(a => a.status === 'OVERDUE' || (new Date(a.deadlineAt) < new Date() && a.status !== 'COMPLETED')).length,
    inProgress: assignments.filter(a => a.status === 'IN_PROGRESS').length,
    notStarted: assignments.filter(a => a.status === 'NOT_STARTED').length,
  };

  return (
    <div style={{ padding: '24px 28px' }}>
      {/* ── Page header — gradient icon tile + title + gradient CTA ── */}
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', marginBottom: 24, gap: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span style={{
            width: 48, height: 48, borderRadius: 14,
            background: 'var(--primary)',
            color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
            boxShadow: '0 6px 16px color-mix(in srgb, var(--primary) 30%, transparent)',
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
              <path d="M6 12v5c3 3 9 3 12 0v-5" />
            </svg>
          </span>
          <div>
            <h3 style={{
              fontSize: 22, fontWeight: 700, color: 'var(--fg)',
              fontFamily: 'var(--font-heading)', letterSpacing: '-0.015em',
            }}>
              Crisis Management Training
            </h3>
            <p style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>
              Define training modules and track completion across your team.
            </p>
          </div>
        </div>
        <button
          onClick={() => setEditTarget('new')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'var(--primary)',
            color: '#fff', border: 'none', borderRadius: 12,
            padding: '11px 20px', fontSize: 14, fontWeight: 600,
            cursor: 'pointer', fontFamily: 'inherit',
            boxShadow: '0 6px 18px color-mix(in srgb, var(--primary) 30%, transparent)',
            transition: 'transform 0.15s, box-shadow 0.15s, filter 0.15s',
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.filter = 'brightness(1.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.filter = 'brightness(1)';
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          New module
        </button>
      </div>

      {/* ── Metrics row — 5 stat cards with icons, animated counts, corner blobs ── */}
      <div className="stagger" style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: 12, marginBottom: 24,
      }}>
        <Metric label="Modules"     value={modules.filter(m => m.isActive).length} icon="book"   tone="primary" />
        <Metric label="Assignments" value={metrics.total}      icon="users"  tone="primary" />
        <Metric label="Completed"   value={metrics.completed}  icon="check"  tone="success" />
        <Metric label="In progress" value={metrics.inProgress} icon="clock"  tone="warning" />
        <Metric label="Overdue"     value={metrics.overdue}    icon="alert"  tone="danger" />
      </div>

      {error && (
        <div style={{
          background: 'rgba(237,28,36,0.10)', border: '1px solid rgba(237,28,36,0.30)',
          color: 'var(--accent-red)', borderRadius: 8, padding: '10px 14px',
          fontSize: 13, marginBottom: 16,
        }}>{error}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.4fr)', gap: 22, alignItems: 'stretch' }}>

        {/* ── LEFT: Module library ── */}
        <section style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>
            Module library
          </div>
          {loading && modules.length === 0 ? (
            <p style={{ color: 'var(--muted)', fontSize: 13, padding: 16 }}>Loading…</p>
          ) : modules.length === 0 ? (
            <div style={{
              border: '2px dashed var(--border-md)', borderRadius: 10,
              padding: '32px 20px', textAlign: 'center', color: 'var(--muted)', fontSize: 13,
            }}>
              No modules yet. Click <strong style={{ color: 'var(--fg)' }}>+ New module</strong> to create your first crisis training.
            </div>
          ) : (
            <div className="stagger" style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1, minHeight: 0, overflowY: 'auto' }}>
              {modules.map(m => {
                const assignedCount = assignments.filter(a => a.moduleId === m.id).length;
                const completedCount = assignments.filter(a => a.moduleId === m.id && a.status === 'COMPLETED').length;
                const completionPct = assignedCount > 0 ? Math.round((completedCount / assignedCount) * 100) : 0;
                return (
                  <ModuleCard
                    key={m.id}
                    module={m}
                    completedCount={completedCount}
                    assignedCount={assignedCount}
                    completionPct={completionPct}
                    onEdit={() => setEditTarget(m.id)}
                    onAssign={() => setAssigningModuleId(m.id)}
                    onToggleArchive={async () => {
                      // Soft-archive: flip is_active. Archived modules
                      // disappear from the staff "My Training" page and
                      // stop being auto-assigned to new staff, but kept
                      // in the DB so existing assignments remain valid
                      // for compliance / audit history.
                      try {
                        await updateModule(m.id, { isActive: !m.isActive });
                        await reload();
                      } catch (err: any) {
                        setError(err.message);
                      }
                    }}
                  />
                );
              })}
            </div>
          )}
        </section>

        {/* ── RIGHT: Assignment dashboard ── */}
        <section style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 8 }}>
            Staff progress
          </div>
          {assignments.length === 0 ? (
            <div style={{
              border: '2px dashed var(--border-md)', borderRadius: 10,
              padding: '32px 20px', textAlign: 'center', color: 'var(--muted)', fontSize: 13,
            }}>
              No assignments yet. Default modules are auto-assigned when you add new staff —
              or use the <strong style={{ color: 'var(--fg)' }}>Assign to staff</strong> button on a module.
            </div>
          ) : (
            <div style={{
              background: 'var(--surface)', border: '1px solid var(--border-md)',
              borderRadius: 10, overflow: 'hidden',
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: 'var(--card)', textAlign: 'left' }}>
                    <th style={th}>Staff</th>
                    <th style={th}>Module</th>
                    <th style={th}>Status</th>
                    <th style={th} title="How much of the training video they've watched">Video&nbsp;watched</th>
                    <th style={th} title="Best quiz score across all attempts">Quiz&nbsp;score</th>
                    <th style={th}>Deadline</th>
                  </tr>
                </thead>
                <tbody>
                  {assignments.map(a => (
                    <AssignmentRow key={a.id} a={a} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {/* ── Module editor modal ── */}
      {editTarget !== null && (
        <ModuleEditor
          moduleId={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={async () => { await reload(); }}
        />
      )}

      {/* ── Assign-to-staff modal ── */}
      {assigningModuleId !== null && (
        <AssignModal
          moduleId={assigningModuleId}
          moduleTitle={modules.find(m => m.id === assigningModuleId)?.title || ''}
          staff={staff}
          existingAssignments={assignments}
          onClose={() => setAssigningModuleId(null)}
          onAssigned={async () => { await reload(); setAssigningModuleId(null); }}
        />
      )}
    </div>
  );
}

// ── Small helpers ───────────────────────────────────────────────────────

const th: React.CSSProperties = {
  padding: '8px 12px', fontSize: 11, fontWeight: 700, color: 'var(--muted)',
  textTransform: 'uppercase', letterSpacing: 0.6,
  borderBottom: '1px solid var(--border)',
};
const td: React.CSSProperties = {
  padding: '10px 12px', fontSize: 12, color: 'var(--fg)',
  borderBottom: '1px solid var(--border)',
};

type MetricTone = 'primary' | 'success' | 'warning' | 'danger';
type MetricIcon = 'book' | 'users' | 'check' | 'clock' | 'alert';

function Metric({ label, value, icon, tone = 'primary' }: {
  label: string;
  value: number | string;
  icon?: MetricIcon;
  tone?: MetricTone;
}) {
  // Tone palette — mirrors the .stat-tile colour quartet on the dashboard
  // so a manager's eye lands on Overdue (red) just like Critical on the
  // dashboard's stat row.
  const toneHex: Record<MetricTone, string> = {
    primary: 'var(--primary)',
    success: 'var(--success)',
    warning: 'var(--warning)',
    danger:  'var(--accent-red)',
  };
  // Figures read foreground-black so the row scans as data — only Completed
  // (green) and Overdue (red) keep their tone as standing signals.
  const colour = tone === 'success' || tone === 'danger' ? toneHex[tone] : 'var(--fg)';
  const Icon = ({ name }: { name: MetricIcon }) => {
    const stroke = 'currentColor';
    const props = { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke, strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
    switch (name) {
      case 'book':  return <svg {...props}><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>;
      case 'users': return <svg {...props}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
      case 'check': return <svg {...props}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>;
      case 'clock': return <svg {...props}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
      case 'alert': return <svg {...props}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>;
    }
  };
  return (
    <div style={{
      background: 'var(--card)', border: '1px solid var(--border)',
      borderRadius: 14, padding: '14px 16px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        {icon && (
          <span style={{ color: 'var(--subtle)', display: 'inline-flex' }}>
            <Icon name={icon} />
          </span>
        )}
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--subtle)' }}>{label}</span>
      </div>
      <div className="num-roll" key={String(value)} style={{
        fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1, color: colour,
      }}>
        {value}
      </div>
    </div>
  );
}

// ── ModuleCard ─────────────────────────────────────────────────────────
// Single module entry in the library list. Shows title + description +
// pass-rate / completion stats + a success-gradient progress bar tied to
// completion%, plus inline Edit / Assign buttons. Right-side badges call
// out DEFAULT modules and missing video so admins can spot config gaps
// at a glance.
function ModuleCard({ module: m, completedCount, assignedCount, completionPct, onEdit, onAssign, onToggleArchive }: {
  module: TrainingModule;
  completedCount: number;
  assignedCount: number;
  completionPct: number;
  onEdit: () => void;
  onAssign: () => void;
  onToggleArchive: () => void | Promise<void>;
}) {
  const hasVideo = !!m.videoUrl;
  const archived = !m.isActive;
  return (
    <div
      onClick={onEdit}
      className="card-refined row-hover"
      style={{
        position: 'relative', cursor: 'pointer', padding: '16px 18px',
        opacity: m.isActive ? 1 : 0.55, overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontWeight: 700, color: 'var(--fg)', fontSize: 15,
            fontFamily: 'var(--font-heading)', letterSpacing: '-0.005em',
          }}>
            {m.title}
          </div>
          {m.description && (
            <div style={{
              fontSize: 12.5, color: 'var(--muted)', marginTop: 5, lineHeight: 1.5,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical' as const,
              overflow: 'hidden',
            }}>
              {m.description}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end', gap: 6, flexShrink: 0 }}>
          {m.isDefault && <PillBadge tone="primary">Default</PillBadge>}
          {archived && <PillBadge tone="muted">Archived</PillBadge>}
          <PillBadge tone={hasVideo ? 'success' : 'danger'}>
            {hasVideo ? 'Video' : 'No video'}
          </PillBadge>
        </div>
      </div>

      {/* Stats line */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 14, marginTop: 12,
        fontSize: 11.5, color: 'var(--muted)',
      }}>
        <span>Pass: <strong style={{ color: 'var(--fg)' }}>{m.passingScore}%</strong></span>
        <span style={{
          width: 4, height: 4, borderRadius: '50%', background: 'var(--subtle)',
        }} />
        <span><strong style={{ color: 'var(--fg)' }}>{completedCount}/{assignedCount}</strong> completed</span>
      </div>

      {/* Progress bar */}
      <div style={{
        marginTop: 8, height: 6, borderRadius: 999,
        background: 'var(--faint)', overflow: 'hidden',
      }}>
        <div style={{
          width: `${completionPct}%`, height: '100%',
          background: completionPct >= 100
            ? 'var(--success)'
            : 'var(--success)',
          transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
        }} />
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button
          onClick={(e) => { e.stopPropagation(); onEdit(); }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            background: 'transparent', border: '1px solid var(--border-md)',
            borderRadius: 8, padding: '5px 11px',
            fontSize: 11.5, fontWeight: 600, color: 'var(--muted)',
            cursor: 'pointer', fontFamily: 'inherit',
            transition: 'background 0.12s, color 0.12s, border-color 0.12s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--primary)';
            e.currentTarget.style.color = '#fff';
            e.currentTarget.style.borderColor = 'var(--primary)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = 'var(--muted)';
            e.currentTarget.style.borderColor = 'var(--border-md)';
          }}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
          </svg>
          Edit
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onAssign(); }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            background: 'transparent', border: '1px solid var(--border-md)',
            borderRadius: 8, padding: '5px 11px',
            fontSize: 11.5, fontWeight: 600, color: 'var(--muted)',
            cursor: 'pointer', fontFamily: 'inherit',
            transition: 'background 0.12s, color 0.12s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--faint)';
            e.currentTarget.style.color = 'var(--fg)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = 'var(--muted)';
          }}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="8.5" cy="7" r="4"/>
            <line x1="20" y1="8" x2="20" y2="14"/>
            <line x1="23" y1="11" x2="17" y2="11"/>
          </svg>
          Assign to staff
        </button>

        {/* Archive / Unarchive — soft-delete that hides the module from
            the staff "My Training" page + stops auto-assigning to new
            staff, while preserving existing assignments for compliance.
            Pushed right so destructive actions don't sit next to Edit. */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            const verb = archived ? 'unarchive' : 'archive';
            const yes = window.confirm(
              archived
                ? `Unarchive "${m.title}"? It will reappear on the staff training page and can be assigned again.`
                : `Archive "${m.title}"? It will disappear from the staff training page and stop being auto-assigned to new staff. Existing assignments stay valid.`
            );
            if (yes) onToggleArchive();
          }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            background: 'transparent', border: '1px solid var(--border-md)',
            borderRadius: 8, padding: '5px 11px',
            fontSize: 11.5, fontWeight: 600, color: 'var(--muted)',
            cursor: 'pointer', fontFamily: 'inherit',
            marginLeft: 'auto',
            transition: 'background 0.12s, color 0.12s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--faint)';
            e.currentTarget.style.color = 'var(--fg)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = 'var(--muted)';
          }}
        >
          {archived ? (
            <>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 7 12 13 21 7"/>
                <path d="M3 7v13h18V7"/>
                <polyline points="8 12 12 8 16 12"/>
              </svg>
              Unarchive
            </>
          ) : (
            <>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="21 8 21 21 3 21 3 8"/>
                <rect x="1" y="3" width="22" height="5"/>
                <line x1="10" y1="12" x2="14" y2="12"/>
              </svg>
              Archive
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// Tinted pill badge — used by ModuleCard for Default / Video / No video / Archived.
// Text-only, solid tint, rounded-rect: purple = Default, green = Video,
// red = No video, grey = Archived.
type PillTone = 'primary' | 'success' | 'danger' | 'muted';
function PillBadge({ children, tone }: { children: React.ReactNode; tone: PillTone }) {
  const palette: Record<PillTone, { bg: string; text: string }> = {
    primary: { bg: 'var(--primary-dim)',   text: 'var(--primary)' },
    success: { bg: 'rgba(22,163,74,0.12)', text: 'var(--success)' },
    danger:  { bg: 'rgba(220,38,38,0.12)', text: 'var(--accent-red)' },
    muted:   { bg: 'var(--faint)',         text: 'var(--muted)' },
  };
  const p = palette[tone];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      background: p.bg, color: p.text,
      fontSize: 11, fontWeight: 700, padding: '4px 10px',
      borderRadius: 6, textTransform: 'uppercase', letterSpacing: 0.6,
      lineHeight: 1.2, whiteSpace: 'nowrap',
    }}>
      {children}
    </span>
  );
}

function AssignmentRow({ a }: { a: TrainingAssignment }) {
  const overdue = new Date(a.deadlineAt) < new Date() && a.status !== 'COMPLETED';
  const statusColour = a.status === 'COMPLETED' ? 'var(--success)'
    : overdue ? 'var(--accent-red)'
    : a.status === 'IN_PROGRESS' ? 'var(--warning)'
    : 'var(--muted)';
  const statusLabel = overdue && a.status !== 'COMPLETED' ? 'OVERDUE' : a.status.replace('_', ' ');
  return (
    <tr className="tr-hover">
      <td style={td}>
        <div style={{ fontWeight: 600 }}>{a.staffName || `Staff #${a.staffId}`}</div>
        <div style={{ fontSize: 10, color: 'var(--muted)' }}>{a.staffEmail}</div>
      </td>
      <td style={td}>{a.moduleTitle}</td>
      <td style={td}>
        <span style={{
          fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
          background: `${statusColour}22`, color: statusColour, letterSpacing: 0.4,
        }}>{statusLabel}</span>
      </td>
      <td style={td}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 60, height: 5, background: 'var(--faint)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{
              width: `${a.videoProgressPercent}%`, height: '100%',
              background: a.videoProgressPercent === 100 ? 'var(--success)' : 'var(--primary)',
            }} />
          </div>
          <span style={{ fontSize: 10, color: 'var(--muted)' }}>{a.videoProgressPercent}%</span>
        </div>
      </td>
      <td style={td}>
        {a.bestQuizScore !== null
          ? <span style={{ fontWeight: 700 }}>{a.bestQuizScore}%</span>
          : <span style={{ color: 'var(--subtle)' }}>—</span>}
        {a.attemptsCount > 0 && (
          <span style={{ fontSize: 10, color: 'var(--subtle)', marginLeft: 4 }}>
            ({a.attemptsCount} attempt{a.attemptsCount === 1 ? '' : 's'})
          </span>
        )}
      </td>
      <td style={{ ...td, color: overdue ? 'var(--accent-red)' : 'var(--muted)' }}>
        {new Date(a.deadlineAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
      </td>
    </tr>
  );
}

// ── Assign-to-staff modal ───────────────────────────────────────────────

function AssignModal({ moduleId, moduleTitle, staff, existingAssignments, onClose, onAssigned }: {
  moduleId: number;
  moduleTitle: string;
  staff: StaffMember[];
  existingAssignments: TrainingAssignment[];
  onClose: () => void;
  onAssigned: () => Promise<void>;
}) {
  const alreadyAssigned = new Set(
    existingAssignments.filter(a => a.moduleId === moduleId).map(a => a.staffId),
  );
  const eligible = staff.filter(s => s.isActive && !alreadyAssigned.has(s.id));
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [deadlineDays, setDeadlineDays] = useState(30);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const toggle = (id: number) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const selectAll = () => setSelected(new Set(eligible.map(s => s.id)));
  const clearAll = () => setSelected(new Set());

  async function submit() {
    if (selected.size === 0) { setError('Pick at least one staff member.'); return; }
    setSubmitting(true);
    setError('');
    try {
      const deadlineAt = new Date();
      deadlineAt.setDate(deadlineAt.getDate() + deadlineDays);
      const { assigned, total } = await assignModule(moduleId, [...selected], deadlineAt.toISOString());
      if (assigned < total) {
        // Some skipped because they were already assigned (race) — still success
      }
      await onAssigned();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div onClick={() => !submitting && onClose()} style={modalBackdrop}>
      <div onClick={e => e.stopPropagation()} style={{ ...modalCard, maxWidth: 540 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 13, marginBottom: 18, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'var(--primary)', color: '#fff',
            boxShadow: '0 6px 16px -4px color-mix(in srgb, var(--primary) 55%, transparent)',
          }}>
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--primary)', marginBottom: 3 }}>
              Assign training
            </div>
            <div style={{ fontFamily: 'var(--font-heading)', fontSize: 18, fontWeight: 700, color: 'var(--fg)', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {moduleTitle}
            </div>
          </div>
        </div>

        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--fg)', marginBottom: 6 }}>
          Deadline (days from today)
        </label>
        <input
          type="number" min={1} max={365} value={deadlineDays}
          onChange={e => setDeadlineDays(parseInt(e.target.value || '30', 10))}
          style={{
            ...inputStyle, marginBottom: 16, width: 120,
          }}
        />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--fg)' }}>
            Staff to assign ({eligible.length} eligible{alreadyAssigned.size ? `, ${alreadyAssigned.size} already assigned` : ''})
          </span>
          <span>
            <button onClick={selectAll} style={miniBtn}>All</button>{' '}
            <button onClick={clearAll} style={miniBtn}>None</button>
          </span>
        </div>
        <div style={{
          maxHeight: 240, overflowY: 'auto',
          border: '1px solid var(--border-md)', borderRadius: 8,
          background: 'var(--surface)', marginBottom: 16,
        }}>
          {eligible.length === 0 ? (
            <p style={{ padding: 14, fontSize: 12, color: 'var(--muted)', textAlign: 'center' }}>
              All active staff are already assigned this module.
            </p>
          ) : (
            eligible.map(s => (
              <label key={s.id} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 12px', borderBottom: '1px solid var(--border)',
                cursor: 'pointer', fontSize: 13, color: 'var(--fg)',
              }}>
                <input
                  type="checkbox" checked={selected.has(s.id)}
                  onChange={() => toggle(s.id)}
                  style={{ accentColor: 'var(--primary)' }}
                />
                <span style={{ flex: 1 }}>{s.firstName} {s.lastName}</span>
                <span style={{ fontSize: 10, color: 'var(--muted)' }}>{s.email}</span>
              </label>
            ))
          )}
        </div>

        {error && <div style={{ color: 'var(--accent-red)', fontSize: 12, marginBottom: 10 }}>⚠ {error}</div>}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={submitting} style={{ ...btnSecondary }}>Cancel</button>
          <button onClick={submit} disabled={submitting || selected.size === 0} style={{
            ...btnPrimary,
            opacity: submitting || selected.size === 0 ? 0.5 : 1,
            cursor: submitting || selected.size === 0 ? 'not-allowed' : 'pointer',
          }}>
            {submitting ? 'Assigning…' : `Assign to ${selected.size} staff`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Shared modal styles ─────────────────────────────────────────────────

export const modalBackdrop: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 10000, padding: 20,
};
export const modalCard: React.CSSProperties = {
  background: 'var(--card)', border: '1px solid var(--border-md)',
  borderRadius: 14, padding: 24, maxWidth: 560, width: '100%',
  boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
  maxHeight: '90vh', overflowY: 'auto',
};
export const inputStyle: React.CSSProperties = {
  padding: '8px 12px', background: 'var(--surface)',
  border: '1px solid var(--border-md)', borderRadius: 8,
  color: 'var(--fg)', fontSize: 13, fontFamily: 'inherit', outline: 'none',
};
export const btnPrimary: React.CSSProperties = {
  background: 'var(--primary)', color: 'var(--on-primary)',
  border: 'none', borderRadius: 8, padding: '9px 22px',
  fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
};
export const btnSecondary: React.CSSProperties = {
  background: 'transparent', border: '1px solid var(--border-md)',
  borderRadius: 8, padding: '9px 18px',
  color: 'var(--fg)', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
};
const miniBtn: React.CSSProperties = {
  background: 'var(--faint)', border: 'none', borderRadius: 4,
  padding: '2px 8px', fontSize: 11, fontWeight: 600, color: 'var(--muted)',
  cursor: 'pointer', fontFamily: 'inherit',
};
